/**
 * Timings for a transcript — audit §3.3, built 28 Sep 2026.
 *
 * **What got built, and what could not.** §3.3 asked for two things: click a
 * line and the video jumps there, and select words to comment with the
 * timecode coming free. Only the second is buildable. The review player is a
 * cross-origin Google Drive iframe with no API, and it ignores a start time
 * in the URL — checked by loading the same film four times side by side as
 * `/preview`, `#t=90`, `?t=90` and `?start=90`, all of which came up on the
 * identical opening frame. Seeking would mean not using Drive's player at
 * all, which is a hosting decision rather than a coding one, and it is
 * Tom's to make.
 *
 * So timings do the other job, the one that removes a bad control: a reader
 * sees where they are, and a comment on a selection carries its own
 * timecode instead of asking somebody to type `00:17` into a box.
 *
 * **Text stays text.** `transcript_versions.content` is unchanged — plain
 * prose — and `transcript_versions.cues` sits beside it carrying the
 * character range each cue covers. That is what keeps the editor, tracked
 * changes, version history and every stored comment working untouched, and
 * why a transcript with no cues is a normal transcript rather than a broken
 * one. Everything here treats cues as optional.
 *
 * **THE PARSING IS NOT HERE.** `public.parse_srt()` in the database is the
 * one implementation — see the note in its migration for why the worker
 * does not do it. This file holds only what the reader needs: turning a
 * passage or a character offset into a timecode. Do not add a second parser
 * to it; two would drift, and the one that drifted would put comments on
 * the wrong second without anybody noticing.
 */

export interface Cue {
  /** Seconds from the start of the media. */
  start: number
  end: number
  /** Where this cue's words begin in the plain text. */
  offset: number
  length: number
}

/** A position in the media, as a number and as it is written. */
export interface Timing {
  seconds: number
  label: string
}

/**
 * The cue a character offset falls in.
 *
 * Falls BACK to the last cue that began before the offset rather than
 * returning nothing, because an offset can legitimately land in the gap
 * between two cues — a paragraph break, a space — and "just after he said
 * that" is the right answer there, not "unknown".
 *
 * Binary search: a two-hour interview is a few thousand cues and this runs
 * on every selection change.
 */
export function cueAt(cues: Cue[] | null | undefined, offset: number): Cue | null {
  if (!cues?.length || !Number.isFinite(offset) || offset < 0) return null

  let low = 0
  let high = cues.length - 1
  let found: Cue | null = null

  while (low <= high) {
    const mid = (low + high) >> 1
    const cue = cues[mid]
    if (cue.offset <= offset) {
      found = cue
      low = mid + 1
    } else {
      high = mid - 1
    }
  }

  return found
}

/**
 * `1:24`, and `1:02:03` once there is an hour to show.
 *
 * No leading zero on the minutes under an hour: this is read as a position
 * in a film, the way a player writes it, not as a duration in a table.
 */
export function timecode(value: number): string {
  if (!Number.isFinite(value) || value < 0) return ''
  const whole = Math.floor(value)
  const hours = Math.floor(whole / 3600)
  const minutes = Math.floor((whole % 3600) / 60)
  const secs = whole % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return hours ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`
}

/** The timecode for a character offset, or '' when there are no timings. */
export function timecodeAt(cues: Cue[] | null | undefined, offset: number): string {
  const cue = cueAt(cues, offset)
  return cue ? timecode(cue.start) : ''
}

/* ------------------------------------------------------- finding a passage -- */

/**
 * How many words of a passage are used to find it again. Enough to be
 * unique in any real transcript, few enough that the pattern below stays
 * small however much text somebody selects. Only the START of a passage is
 * wanted anyway — that is the second being asked about.
 */
const ANCHOR_WORDS = 12

function escape(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Where a passage begins in the text the cues were measured against.
 *
 * Whitespace-tolerant, because the two sides genuinely differ: the stored
 * content separates paragraphs with a newline, while a selection read out
 * of the editor arrives with those breaks flattened to spaces. Matching on
 * the exact characters would quietly fail on any selection that crossed a
 * paragraph — the commonest kind.
 */
function findPassage(haystack: string, passage: string): number {
  const words = passage.trim().split(/\s+/).filter(Boolean).slice(0, ANCHOR_WORDS)
  if (!words.length) return -1

  /* The cheap exact case first: an unedited transcript answers here. */
  const direct = haystack.indexOf(words.join(' '))
  if (direct !== -1) return direct

  const match = new RegExp(words.map(escape).join('\\s+')).exec(haystack)
  return match ? match.index : -1
}

/**
 * When a passage was said, found by its WORDS rather than by its position.
 *
 * Position would have been simpler and is wrong. Cue offsets are measured
 * against one particular revision, and the moment a reader corrects a word
 * near the top every offset below it shifts — silently, and by an amount
 * nothing on the page could show. Searching for the words instead means an
 * edit elsewhere in the transcript cannot move anybody else's timecode, and
 * a passage whose own words have been rewritten simply has no timecode any
 * more, which is the honest answer rather than a confidently wrong one.
 *
 * `cueText` is the content of the revision the cues belong to — NOT the
 * text currently on screen.
 */
export function timingForPassage(
  cues: Cue[] | null | undefined,
  cueText: string | null | undefined,
  passage: string,
): Timing | null {
  if (!cues?.length || !cueText || !passage.trim()) return null

  const at = findPassage(cueText, passage)
  if (at === -1) return null

  const cue = cueAt(cues, at)
  if (!cue) return null

  return { seconds: cue.start, label: timecode(cue.start) }
}

/**
 * Reads whatever came out of the database into cues, or [] if it is not
 * cues. Stored JSON is data, not a promise: a hand-edited row, an older
 * shape or a null all have to land somewhere sensible, and a reader that
 * throws on a malformed cue would take the whole transcript down with it.
 */
export function readCues(raw: unknown): Cue[] {
  if (!Array.isArray(raw)) return []
  const cues: Cue[] = []
  for (const item of raw) {
    const row = item as Record<string, unknown>
    const start = Number(row?.start)
    const offset = Number(row?.offset)
    const length = Number(row?.length)
    if (!Number.isFinite(start) || !Number.isFinite(offset) || !Number.isFinite(length)) continue
    const end = Number(row?.end)
    cues.push({ start, end: Number.isFinite(end) ? end : start, offset, length })
  }
  return cues.sort((a, b) => a.offset - b.offset)
}
